import type { EnterpriseThemeContract } from "@bestowinc/kindred-ds";
import { SHELL_CONFIG_ELEMENT_ID } from "@bestowinc/tinkertoy-core";
import { z } from "zod";

/**
 * Dev-only bundle overrides forwarded from the host's `ttv2-dev-override`
 * cookie. Each entry points the browser at a locally-served bundle so a host's
 * client renders the SAME local bundle its server already loads — without this,
 * the server honors the override but the browser still fetches the CDN.
 */
const bundleOverrideSchema = z.object({ bundleUrl: z.string() });
const devBundleOverridesSchema = z.object({
  views: z.record(bundleOverrideSchema),
  modules: z.record(bundleOverrideSchema),
});

export type DevBundleOverrides = z.infer<typeof devBundleOverridesSchema>;

const serializedProductAnalyticsConfigSchema = z.object({
  enabled: z.boolean(),
  metadata: z.record(z.unknown()),
});

export type SerializedProductAnalyticsConfig = z.infer<
  typeof serializedProductAnalyticsConfigSchema
>;

const enterpriseThemeSchema = z.custom<EnterpriseThemeContract>(
  value =>
    typeof value === "object" && value !== null && !Array.isArray(value),
);

const environmentSchema = z.record(z.string().optional());

const wireSerializedShellConfigSchema = z.object({
  apiBaseUrl: z.string(),
  cdnBaseUrl: z.string(),
  theme: enterpriseThemeSchema,
  analytics: z.unknown().optional(),
  env: z.unknown().optional(),
  devOverrides: z.unknown().optional(),
  defaultDocumentTitle: z.string().nullish(),
});

/**
 * Per-host runtime config a host app serializes into the shell HTML for the
 * browser entry to read on boot.
 *
 * The shell bundle is built once and served verbatim by every host
 * (enrollment-composer, verify, the microsites, ...), so the values that differ
 * per host travel here rather than being baked into the bundle:
 *
 * - `apiBaseUrl` — the host's same-origin Tinkertoy API mount. The shell fetches
 *   `${apiBaseUrl}/load` and posts `${apiBaseUrl}/action` against it. EC mounts
 *   this at `/composer/tinkertoy`; the standalone demo uses `/api/tinkertoy`.
 * - `cdnBaseUrl` — the CDN the module loader resolves view/module bundles from.
 * - `theme` — the resolved carrier theme handed to the shell's `ThemeProvider`.
 *   Resolved per request per carrier by the host, mirroring how EC, verify, and
 *   the microsites each resolve their own theme and hand it in (see
 *   {@link ShellConfig}).
 *
 * @see SHELL_CONFIG_ELEMENT_ID for the injection contract.
 */
export interface SerializedShellConfig {
  apiBaseUrl: string;
  cdnBaseUrl: string;
  theme: EnterpriseThemeContract;
  analytics?: SerializedProductAnalyticsConfig;
  /**
   * Optional shared runtime env vars the host exposes as the browser global
   * `ENV` (`MODE`/`ORG_CODE`/`PARTNER_ID`, …). The shell entry assigns this to
   * `globalThis.ENV` so bundled code that reads a Remix-style `window.ENV`
   * works. Absent for the standalone demo, whose entry then leaves `ENV` unset.
   */
  env?: Record<string, string | undefined>;
  /**
   * Optional dev-only bundle overrides (from the host's `ttv2-dev-override`
   * cookie). The shell entry threads these into the module/view loaders so the
   * browser loads locally-served bundles for overridden ids. Absent in normal
   * operation and for the standalone demo.
   */
  devOverrides?: DevBundleOverrides;
  /**
   * Carrier-appropriate base document title for pre-flow errors and the initial
   * `/load` in-flight window. Injected by the host alongside the serialized
   * config so {@link ShellErrorBoundary} can fall back when `flowMeta` is absent.
   */
  defaultDocumentTitle?: string;
}

function normalizeSerializedShellConfig(
  config: z.infer<typeof wireSerializedShellConfigSchema>,
): SerializedShellConfig {
  const {
    analytics,
    defaultDocumentTitle,
    devOverrides,
    env,
    ...requiredConfig
  } = config;
  const parsedAnalytics =
    serializedProductAnalyticsConfigSchema.safeParse(analytics);
  const parsedDevOverrides = devBundleOverridesSchema.safeParse(devOverrides);
  const parsedEnvironment = environmentSchema.safeParse(env);

  return {
    ...requiredConfig,
    ...(typeof defaultDocumentTitle === "string"
      ? { defaultDocumentTitle }
      : {}),
    ...(parsedAnalytics.success ? { analytics: parsedAnalytics.data } : {}),
    ...(parsedEnvironment.success ? { env: parsedEnvironment.data } : {}),
    ...(parsedDevOverrides.success
      ? { devOverrides: parsedDevOverrides.data }
      : {}),
  };
}

/**
 * Read the host-injected {@link SerializedShellConfig} from the DOM.
 *
 * Returns `null` when the config element is absent or malformed — which is the
 * expected case for the standalone demo shell, whose entry then falls back to
 * its own demo defaults. A present-but-malformed blob also yields `null` rather
 * than throwing, so a serialization bug degrades to the fallback instead of a
 * white screen with no shell mounted at all.
 *
 * Browser telemetry settings in the same JSON blob are ignored; the shell entry
 * parses them separately.
 */
export function readSerializedShellConfig(): SerializedShellConfig | null {
  const element = document.getElementById(SHELL_CONFIG_ELEMENT_ID);
  if (!element?.textContent) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(element.textContent);
    const config = wireSerializedShellConfigSchema.safeParse(parsed);
    if (!config.success) {
      return null;
    }

    return normalizeSerializedShellConfig(config.data);
  } catch {
    return null;
  }
}
